import { AsyncStatusEnum } from 'src/researcher/types/common';
import { ListProjectsAPIResponseProjectDetail } from 'src/researcher/types/project';

export type ProjectCardData = ListProjectsAPIResponseProjectDetail & {
  downloading: boolean;
  deleting: boolean;
  restoring: boolean;
};

export type ProjectsCollection = Record<string, ProjectCardData>;
export type SortByOrderBy =
  | 'name:asc'
  | 'name:desc'
  | 'size:asc'
  | 'size:desc'
  | 'date:asc'
  | 'date:desc';

export type ListStore = {
  query: string;
  skip: number;
  total: number;
  status: AsyncStatusEnum;
  projects: ProjectsCollection;
  count: number;
  sort: SortByOrderBy;
  forceReloadCount: number;
};

export type ListProjectContext = 'recent' | 'trash';

type AddProjectsListStorePayload = {
  total: number;
  projects: ListProjectsAPIResponseProjectDetail[];
  context: ListProjectContext;
};

type AddProjectsListStoreAction = {
  type: 'add_projects';
  payload: AddProjectsListStorePayload;
};

type SetLoadingStatusListStoreAction = {
  type: 'set_loading_status';
  payload: { status: AsyncStatusEnum; context: ListProjectContext };
};

type LoadNextListStoreAction = {
  type: 'load_next';
  payload: { context: ListProjectContext };
};

type SetStatusProjectCardListStoreActionPayload = {
  id: string;
  context: ListProjectContext;
  // state: ProjectStateStatusEnum;
};

type SetStatusProjectCardListStoreAction = {
  type: 'set_status';
  payload: SetStatusProjectCardListStoreActionPayload;
};

type SetDownloadingProjectCardListStoreActionPayload = {
  id: string;
  downloading: boolean;
  context: ListProjectContext;
};

type SetDownloadingProjectCardListStoreAction = {
  type: 'set_downloading';
  payload: SetDownloadingProjectCardListStoreActionPayload;
};

type SetDeletingProjectCardListStoreActionPayload = {
  id: string;
  deleting: boolean;
  context: ListProjectContext;
};

type SetDeletingProjectCardListStoreAction = {
  type: 'set_deleting';
  payload: SetDeletingProjectCardListStoreActionPayload;
};

type DeleteProjectListStoreAction = {
  type: 'delete_project';
  payload: { id: string; context: ListProjectContext; shouldRefreshCorrespondingContext: boolean };
};

type SetRestoringProjectCardListStoreActionPayload = {
  id: string;
  restoring: boolean;
  context: ListProjectContext;
};

type RestoreProjectListStoreAction = {
  type: 'restore_project';
  payload: SetRestoringProjectCardListStoreActionPayload;
};

type StartNewQuerySearchAction = {
  type: 'start_new_query';
  payload: { query: string; context: ListProjectContext };
};

type SortCurrentListAction = {
  type: 'sort_current_list';
  payload: { sort: SortByOrderBy; context: ListProjectContext };
};

type ResetStoreListStoreAction = {
  type: 'reset';
  payload: { context: ListProjectContext };
};

export type ListActions =
  | AddProjectsListStoreAction
  | SetLoadingStatusListStoreAction
  | LoadNextListStoreAction
  | SetStatusProjectCardListStoreAction
  | SetDownloadingProjectCardListStoreAction
  | SetDeletingProjectCardListStoreAction
  | DeleteProjectListStoreAction
  | ResetStoreListStoreAction
  | RestoreProjectListStoreAction
  | StartNewQuerySearchAction
  | SortCurrentListAction;

export const addProjectsListStoreAction = (
  payload: {
    total: number;
    projects: ListProjectsAPIResponseProjectDetail[];
  },
  context: ListProjectContext
): AddProjectsListStoreAction => ({ type: 'add_projects', payload: { ...payload, context } });

export const loadNextListStoreAction = (context: ListProjectContext): LoadNextListStoreAction => ({
  type: 'load_next',
  payload: { context },
});

export const resetStoreListStoreAction = (
  context: ListProjectContext
): ResetStoreListStoreAction => ({
  type: 'reset',
  payload: { context },
});

export const setLoadingStatusListStoreAction = (
  status: AsyncStatusEnum,
  context: ListProjectContext
): SetLoadingStatusListStoreAction => ({
  type: 'set_loading_status',
  payload: { status, context },
});

export const setDownloadingProjectCardListStoreAction = (
  payload: SetDownloadingProjectCardListStoreActionPayload
): SetDownloadingProjectCardListStoreAction => ({
  type: 'set_downloading',
  payload,
});

export const setDeletingProjectCardListStoreAction = (
  payload: SetDeletingProjectCardListStoreActionPayload
): SetDeletingProjectCardListStoreAction => ({
  type: 'set_deleting',
  payload,
});

export const setStatusProjectCardListStoreAction = (
  payload: SetStatusProjectCardListStoreActionPayload
): SetStatusProjectCardListStoreAction => ({
  type: 'set_status',
  payload,
});

export const deleteProjectFromListAction = (
  id: string,
  context: ListProjectContext,
  shouldRefreshCorrespondingContext: boolean
): DeleteProjectListStoreAction => ({
  type: 'delete_project',
  payload: { id, context, shouldRefreshCorrespondingContext },
});

export const restoreProjectFromListAction = (
  payload: SetRestoringProjectCardListStoreActionPayload
): RestoreProjectListStoreAction => ({
  type: 'restore_project',
  payload,
});

export const startNewQuerySearchAction = (query: string, context: ListProjectContext) => ({
  type: 'start_new_query',
  payload: { query, context },
});

export const sortCurrentListAction = (sort: SortByOrderBy, context: ListProjectContext) => ({
  type: 'sort_current_list',
  payload: { sort, context },
});

export const forcedReloadListAction = (context: ListProjectContext) => ({
  type: 'forced_list',
  payload: { context },
});

export type LandingListStore = {
  recent: ListStore;
  trash: ListStore;
};

const listStateInitVal: ListStore = {
  query: '',
  skip: 0,
  total: 0,
  projects: {},
  count: 0,
  status: AsyncStatusEnum.PENDING,
  sort: 'date:desc',
  forceReloadCount: 0,
};

const InitLandingStoreState = {
  recent: { ...listStateInitVal },
  trash: { ...listStateInitVal },
};

const transformProjectDataToCardData = (
  project: ListProjectsAPIResponseProjectDetail
): ProjectCardData => ({
  ...project,
  // state: ProjectStateStatusEnum.NORMAL,
  downloading: false,
  deleting: false,
  restoring: false,
});

const createProjectCollection = (projects: ListProjectsAPIResponseProjectDetail[] = []) =>
  projects?.map?.(transformProjectDataToCardData)?.reduce((obj: ProjectsCollection, project) => {
    obj[project.id] = project;
    return obj;
  }, {} as ProjectsCollection);

export function listStoreReducer(
  store: LandingListStore = InitLandingStoreState,
  action: ListActions
) {
  const context = action.payload?.context;

  switch (action.type) {
    case 'add_projects': {
      /**
       * Add project to state context
       */

      const state = store[context];
      const newState = {
        ...state,
        projects: {
          ...state.projects,
          ...createProjectCollection(action.payload.projects),
        },
        count: state.count + action.payload.projects?.length || 0,
        total: action.payload.total,
        status: AsyncStatusEnum.RESOLVE,
      };

      return {
        ...store,
        [context]: newState,
      };
    }

    case 'set_loading_status': {
      /**
       * Add projects to loading status
       */
      const state = store[context];
      const newState = { ...state, status: action.payload.status };
      return {
        ...store,
        [context]: newState,
      };
    }

    case 'load_next': {
      const state = store[context];
      /**
       * There are some project in list so load next
       */
      if (state.count < state.total && state.status === AsyncStatusEnum.RESOLVE) {
        const newState = {
          ...state,
          status: AsyncStatusEnum.PENDING,
          skip: Math.min(state.count, state.total),
        };
        return {
          ...store,
          [context]: newState,
        };
      }
      /**
       * return simple store
       */
      return store;
    }

    case 'set_downloading': {
      const state = store[context];
      const project = state.projects[action.payload.id];
      /**
       * project not found so ignore update
       */
      if (!project) {
        return store;
      }
      /**
       * create new projects in current context
       */
      const newProjects = { ...state.projects };

      newProjects[action.payload.id] = {
        ...project,
        downloading: action.payload.downloading,
      };
      /**
       * new context for the store
       */
      const newState = {
        ...state,
        projects: newProjects,
      };

      return {
        ...store,
        [context]: newState,
      };
    }

    case 'restore_project': {
      const state = store[context];
      const project = state.projects[action.payload.id];
      /**
       * project not found so ignore update
       */
      if (!project) {
        return store;
      }
      /**
       * create new projects in current context
       */
      const newProjects = { ...state.projects };

      newProjects[action.payload.id] = {
        ...project,
        restoring: action.payload.restoring,
      };

      /**
       *  new context for the store
       */
      const newState = {
        ...state,
        projects: newProjects,
      };

      return {
        ...store,
        [context]: newState,
      };
    }

    case 'set_deleting': {
      const state = store[context];
      const project = state.projects[action.payload.id];
      /**
       * project not found so ignore update
       */
      if (!project) {
        return store;
      }
      /**
       * create new projects in current context
       */
      const newProjects = { ...state.projects };

      newProjects[action.payload.id] = {
        ...project,
        deleting: action.payload.deleting,
      };
      /**
       *  new context for the store
       */
      const newState = {
        ...state,
        projects: newProjects,
      };

      return {
        ...store,
        [context]: newState,
      };
    }

    case 'delete_project': {
      const state = store[context];
      const project = state.projects[action.payload.id];
      /**
       * project not found so ignore update
       */
      if (!project) {
        return store;
      }
      /**
       * create new projects in current context
       */
      const newProjects = { ...state.projects };
      /** delete this project */
      delete newProjects[action.payload.id];
      /**
       *  new context for the store
       */
      const newState = {
        ...state,
        projects: newProjects,
        count: Math.max(state.count - 1, 0),
        total: Math.max(state.total - 1, 0),
      };
      /**
       * REFRESH OTHER CONTEXT STATE WHERE THIS PROJECT DELETE WILL END UP
       */
      const correspondingContext = context === 'recent' ? 'trash' : 'recent';
      let correspondingContextState = store[correspondingContext];
      if (action.payload.shouldRefreshCorrespondingContext) {
        correspondingContextState = {
          ...listStateInitVal,
          forceReloadCount: correspondingContextState.forceReloadCount + 1,
        };
      }
      return {
        ...store,
        [correspondingContext]: correspondingContextState,
        [context]: newState,
      };
    }

    case 'start_new_query': {
      const state = store[context];
      if (action.payload.query !== state.query) {
        const newState = {
          ...listStateInitVal,
          status: state.status,
          query: action.payload.query,
        };
        return { ...store, [context]: newState };
      }
      return store;
    }

    case 'sort_current_list': {
      const state = store[context];
      if (action.payload.sort !== state.sort) {
        const newState = {
          ...listStateInitVal,
          status: state.status,
          query: store[context].query,
          sort: action.payload.sort,
        };
        return { ...store, [context]: newState };
      }
      return store;
    }

    case 'reset': {
      return {
        ...store,
        [context]: InitLandingStoreState[context],
      };
    }

    default: {
      return store;
    }
  }
}
